// The rows-out half of a navigation, shared by the bar charts that opt into `motion: "navigation"`.
//
// A navigation replaces one set of rows with another, so the rows it replaces animate OUT first and
// the new ones grow in after. Left to itself ECharts pairs the old bars with the new ones by
// position and morphs each into its replacement, which reads as the numbers changing under fixed
// labels rather than as moving to a different view.
import type { ChartSettingsType } from "@/features/Charts/types/ChartType";

/** What a draw put on screen, which is what the next navigation has to animate away. */
export type DrawnSeries = { id: string; itemIds: (string | undefined)[] };

/**
 * Whether this draw is a navigation whose predecessor should leave first: the caller opted in, the
 * reader has not asked for less motion, something is on screen to animate, and the rows themselves
 * are changing. A value-only update (a stage toggled off, say) is one movement, not a handover.
 */
export function shouldHandOverRows({
  settings,
  reducedMotion,
  drawnCategories,
  drawnSeries,
  nextCategories,
}: {
  settings: Pick<ChartSettingsType, "motion">;
  reducedMotion: boolean;
  drawnCategories: string[];
  drawnSeries: DrawnSeries[];
  nextCategories: string[];
}): boolean {
  if (settings.motion !== "navigation" || reducedMotion) return false;
  if (drawnSeries.length === 0) return false;
  return (
    drawnCategories.length !== nextCategories.length || drawnCategories.some((label, index) => label !== nextCategories[index])
  );
}

/**
 * The series option that carries those rows back to the axis: the same series, holding the same
 * items, at zero.
 *
 * Naming the items is the point. ECharts diffs a series' data by item id when the items carry one,
 * so id-less zeros read as a wholesale replacement and the bars vanish in a single frame instead of
 * shrinking — indistinguishable from no animation at all.
 */
export function zeroedSeries(drawnSeries: DrawnSeries[]) {
  return drawnSeries.map((series) => ({
    id: series.id,
    data: series.itemIds.map((itemId) => ({ value: 0, id: itemId })),
  }));
}
